import React, { useMemo } from "react";
import { Check, Clipboard, Clapperboard, FileText, Loader2, Sparkles, Video, X } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";

const asList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const getSuggestedRefs = (advice) => advice?.director_suggested_refs || advice?.suggested_refs || {};

const contextLabel = (value) => {
  const labels = {
    OPENING: "起始镜头",
    SAME_SCENE: "同场连续",
    NEW_SCENE: "新场景",
    TIME_JUMP: "时间跳跃",
    INSERT: "插入镜头",
    REACTION: "反应镜头",
    MONTAGE: "蒙太奇",
    NONE: "不继承",
    CHARACTER_ONLY: "只继承人物",
    CHARACTER_EMOTION_ONLY: "人物与情绪",
    CHARACTER_SCENE_LIGHTING: "人物、场景、光影",
    PROP_OR_DETAIL_ONLY: "道具或细节",
  };
  return labels[value] || value || "未判断";
};

const PromptBlock = ({ title, icon: Icon, value, onApply, applyLabel, accentColor }) => {
  const toast = useToast();

  const handleCopy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success("已复制");
  };

  return (
    <section className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
      <div className="h-9 px-3 flex items-center justify-between border-b" style={{ borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={14} style={{ color: accentColor }} />
          <span className="text-xs font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            disabled={!value}
            className="w-7 h-7 rounded-md flex items-center justify-center transition-colors disabled:opacity-40 hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: "var(--color-text-secondary)" }}
            title="复制"
            aria-label={`复制${title}`}
          >
            <Clipboard size={13} />
          </button>
          <button
            onClick={onApply}
            disabled={!value}
            className="h-7 px-2 rounded-md flex items-center gap-1 text-2xs font-medium transition-colors disabled:opacity-40"
            style={{ backgroundColor: `${accentColor}18`, color: accentColor, border: `1px solid ${accentColor}45` }}
          >
            <Check size={12} />
            {applyLabel}
          </button>
        </div>
      </div>
      <textarea
        readOnly
        value={value || ""}
        className="w-full min-h-[118px] p-3 text-xs leading-5 resize-none outline-none custom-scrollbar"
        style={{ backgroundColor: "transparent", color: "var(--color-text-secondary)" }}
        placeholder="暂无建议"
      />
    </section>
  );
};

const RefGroup = ({ label, items, color }) => (
  <div className="space-y-1.5">
    <div className="text-2xs font-semibold" style={{ color: "var(--color-text-muted)" }}>{label}</div>
    {items.length > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item}
            className="px-2 py-1 rounded-md text-2xs"
            style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}35` }}
          >
            {item}
          </span>
        ))}
      </div>
    ) : (
      <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>无</div>
    )}
  </div>
);

const DirectorAdvicePanel = ({
  isOpen,
  onClose,
  loading,
  error,
  advice,
  shotIndex,
  onApplyImagePrompt,
  onApplyVideoPrompt,
  onApplySeedancePrompt,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const notes = asList(advice?.director_notes);
  const warnings = asList(advice?.missing_ref_warnings);
  const refs = getSuggestedRefs(advice);
  const characters = useMemo(() => asList(refs.characters), [refs.characters]);
  const scenes = useMemo(() => asList(refs.scenes), [refs.scenes]);
  const props = useMemo(() => asList(refs.props), [refs.props]);
  const context = advice?.director_context_snapshot || {};
  const directorContext = advice?.director_context || {};
  const scriptSlice = context.script_slice || "";

  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  if (!mounted) return null;

  return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      <aside
        className={`absolute right-0 top-0 h-full w-[calc(100vw_-_64px)] max-w-[560px] md:w-[460px] xl:w-[560px] pointer-events-auto flex flex-col border-l shadow-2xl ${exiting ? "animate-out slide-out-to-right-4 duration-150" : "animate-in slide-in-from-right duration-200"}`}
        style={{
          backgroundColor: "var(--color-bg-secondary)",
          borderColor: "var(--color-border-primary)",
          boxShadow: "var(--shadow-modal)",
        }}
      >
        <div className="shrink-0 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex items-center gap-2">
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${theme.colors.purplePrimary}18`, color: theme.colors.purplePrimary }}
              >
                <Clapperboard size={16} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                  导演建议
                </div>
                <div className="text-2xs truncate" style={{ color: "var(--color-text-muted)" }}>
                  {shotIndex != null ? `镜头 #${shotIndex + 1}` : "当前镜头"} · 只生成建议和提示词
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-[var(--color-bg-tertiary)]"
              style={{ color: "var(--color-text-secondary)" }}
              aria-label="关闭导演建议"
              title="关闭"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 space-y-3">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <Loader2 size={20} className="animate-spin" style={{ color: theme.colors.purplePrimary }} />
              <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>导演正在读取剧本和分镜上下文</div>
            </div>
          ) : error ? (
            <div className="rounded-lg border p-4 text-xs" style={{ borderColor: "var(--color-error)", color: "var(--color-text-secondary)" }}>
              {error}
            </div>
          ) : (
            <>
              <section className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  <Sparkles size={14} style={{ color: theme.colors.purplePrimary }} />
                  导演笔记
                </div>
                {notes.length > 0 ? (
                  <div className="space-y-1.5">
                    {notes.map((note, index) => (
                      <div key={`${note}-${index}`} className="text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>
                        {index + 1}. {note}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>暂无建议</div>
                )}
              </section>

              <section className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  <Clapperboard size={14} style={{ color: theme.colors.accentPrimary }} />
                  镜头关系
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-md px-2 py-1.5" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>转场</div>
                    <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                      {contextLabel(directorContext.transition_type)}
                    </div>
                  </div>
                  <div className="rounded-md px-2 py-1.5" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                    <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>视觉继承</div>
                    <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
                      {contextLabel(directorContext.visual_carryover)}
                    </div>
                  </div>
                </div>
                <div className="text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>
                  {directorContext.relation_note || "暂无镜头关系判断"}
                </div>
              </section>

              <PromptBlock
                title="生图提示词建议"
                icon={FileText}
                value={advice?.director_image_prompt}
                onApply={onApplyImagePrompt}
                applyLabel="应用生图"
                accentColor={theme.colors.accentPrimary}
              />

              <PromptBlock
                title="视频提示词建议"
                icon={Video}
                value={advice?.director_video_prompt}
                onApply={onApplyVideoPrompt}
                applyLabel="应用视频"
                accentColor={theme.colors.bluePrimary}
              />

              <PromptBlock
                title="演员/场景参考演戏提示词"
                icon={Clapperboard}
                value={advice?.director_seedance_prompt}
                onApply={onApplySeedancePrompt}
                applyLabel="应用到视频"
                accentColor={theme.colors.purplePrimary}
              />

              <section className="rounded-lg border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>建议检查的参考图</div>
                <RefGroup label="人物" items={characters} color={theme.colors.accentPrimary} />
                <RefGroup label="场景" items={scenes} color={theme.colors.bluePrimary} />
                <RefGroup label="道具" items={props} color={theme.colors.greenPrimary} />
                {warnings.length > 0 && (
                  <div className="pt-2 border-t space-y-1.5" style={{ borderColor: "var(--color-border-primary)" }}>
                    {warnings.map((warning, index) => (
                      <div key={`${warning}-${index}`} className="text-2xs leading-5" style={{ color: "var(--color-warning)" }}>
                        {warning}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {scriptSlice && (
                <section className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
                  <div className="h-9 px-3 flex items-center gap-2 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
                    <FileText size={14} style={{ color: "var(--color-text-muted)" }} />
                    <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>本次参考剧本片段</span>
                  </div>
                  <pre className="max-h-44 overflow-y-auto custom-scrollbar p-3 text-2xs leading-5 whitespace-pre-wrap break-words font-sans" style={{ color: "var(--color-text-muted)" }}>
                    {scriptSlice}
                  </pre>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default DirectorAdvicePanel;
